import {
  collection,
  deleteField,
  doc,
  DocumentData,
  onSnapshot,
  orderBy,
  query,
  writeBatch,
} from "firebase/firestore";
import { firebaseStore } from "./firestore";

export const PRIVATE_CUSTOMERS_COLLECTION = "customers_private";

export type CustomerCategory = "store" | "retail" | "other" | "private";
export type CustomerPriceGroup = "store" | "restaurant" | "retail" | "other";

export interface DashboardCustomer {
  firebaseId: string;
  id: string;
  name: string;
  address: string;
  image: string;
  url: string;
  category: CustomerCategory;
  isPublic: boolean;
  active: boolean;
  priceGroup?: CustomerPriceGroup;
  priceOverrides: Record<string, number>;
  createdAt: number;
}

export type PublicCustomerData = Pick<DashboardCustomer,
  "id" | "name" | "address" | "image" | "url" | "category" | "isPublic">;
export type PrivateCustomerData = Pick<DashboardCustomer,
  "active" | "priceGroup" | "priceOverrides" | "createdAt">;

// The public document ID is also the private document ID. The public `id`
// remains stable because weekly plans and stock distributions store that ID.
export function mergeCustomer(firebaseId: string, publicData: DocumentData, privateData?: DocumentData): DashboardCustomer {
  return {
    firebaseId,
    id: publicData.id || firebaseId,
    name: publicData.name || "",
    address: publicData.address || "",
    image: publicData.image || "",
    url: publicData.url || "",
    category: publicData.category || "other",
    isPublic: publicData.isPublic !== false,
    // Legacy fallback keeps dashboard workflows working until migration runs.
    active: (privateData?.active ?? publicData.active) !== false,
    priceGroup: privateData?.priceGroup ?? publicData.priceGroup,
    priceOverrides: privateData?.priceOverrides ?? publicData.priceOverrides ?? {},
    createdAt: privateData?.createdAt ?? publicData.createdAt ?? 0,
  };
}

export function subscribeDashboardCustomers(
  onCustomers: (customers: DashboardCustomer[]) => void,
  onError: (error: Error) => void
): () => void {
  let publicRows: Array<{ firebaseId: string; data: DocumentData }> | null = null;
  let privateRows: Map<string, DocumentData> | null = null;
  const emit = () => {
    if (!publicRows || !privateRows) return;
    onCustomers(publicRows.map(({ firebaseId, data }) => mergeCustomer(firebaseId, data, privateRows?.get(firebaseId))));
  };

  const stopPublic = onSnapshot(query(collection(firebaseStore, "customers"), orderBy("name")), (snapshot) => {
    publicRows = snapshot.docs.map((item) => ({ firebaseId: item.id, data: item.data() }));
    emit();
  }, onError);
  const stopPrivate = onSnapshot(collection(firebaseStore, PRIVATE_CUSTOMERS_COLLECTION), (snapshot) => {
    privateRows = new Map(snapshot.docs.map((item) => [item.id, item.data()]));
    emit();
  }, onError);
  return () => { stopPublic(); stopPrivate(); };
}

export async function saveDashboardCustomer(
  publicData: PublicCustomerData,
  privateData: PrivateCustomerData,
  existingFirebaseId?: string
): Promise<string> {
  const customerRef = existingFirebaseId
    ? doc(firebaseStore, "customers", existingFirebaseId)
    : doc(collection(firebaseStore, "customers"));
  const privateRef = doc(firebaseStore, PRIVATE_CUSTOMERS_COLLECTION, customerRef.id);
  const batch = writeBatch(firebaseStore);
  if (existingFirebaseId) {
    batch.update(customerRef, {
      ...publicData,
      // An edited legacy customer is split atomically, even before migration.
      active: deleteField(), priceGroup: deleteField(),
      priceOverrides: deleteField(), createdAt: deleteField(),
    });
  } else {
    batch.set(customerRef, publicData);
  }
  batch.set(privateRef, {
    ...privateData,
    customerDocId: customerRef.id,
    customerId: publicData.id,
  });
  await batch.commit();
  return customerRef.id;
}

export async function deleteDashboardCustomer(firebaseId: string): Promise<void> {
  const batch = writeBatch(firebaseStore);
  batch.delete(doc(firebaseStore, "customers", firebaseId));
  batch.delete(doc(firebaseStore, PRIVATE_CUSTOMERS_COLLECTION, firebaseId));
  await batch.commit();
}
